import { ChannelKindType } from '../channels';
import { IField } from '../form';
import { ValidatorResult } from '../validator';

import { MarkupServer } from './server';

export const MARKUP_ID = 'markup_id';

export enum MarkupFeature {
  markup = 'markup',
  vars = 'vars',
  emojis = 'emojis',
  attachments = 'attachments',
  /** @-mention оператора; в дефолтный markupFeaturesOrder чата не входит */
  operatorMention = 'operatorMention'
}

export const markupFeaturesOrder: MarkupFeature[] = [
  MarkupFeature.markup,
  MarkupFeature.vars,
  MarkupFeature.emojis,
  MarkupFeature.attachments
];

export type MarkupCustomEmojiChannel = {
  id: string;
  kind: ChannelKindType;
};

/**
 * Вкладка «Кастомные» в окне выбора emoji. Без параметров вкладки нет: так
 * возможность не появляется в кнопках, прочих блоках и в чужих полях ввода.
 */
export type MarkupCustomEmojiParams = {
  /** Канал диалога — для подсказки о доставке; `null` в редакторе блока сценария */
  channel: MarkupCustomEmojiChannel | null;
  /**
   * Разметка с `tg-emoji` по текущему содержимому поля; `null` — custom emoji
   * в тексте нет. Поле ответа отправляет её как `message_markup`, редактор
   * блока разметку хранит сам и колбэк не передаёт.
   */
  onMarkupChange?: (markup: string | null) => void;
  /**
   * Включена ли в блоке разметка Markdown: тогда custom emoji уходят обычными
   * заменами во все мессенджеры, и подсказка у вкладки об этом предупреждает.
   */
  parseMarkdown?: boolean;
};

export interface IMarkup {
  readonly features: MarkupFeature[];

  readonly hasVarsFeature: boolean;
  readonly hasMarkupFeature: boolean;
  readonly hasEmojisFeature: boolean;
  readonly hasOperatorMentionFeature: boolean;
  /** Показывать ли вкладку «Кастомные» в окне выбора emoji */
  readonly hasCustomEmojiTab: boolean;
  readonly customEmoji: MarkupCustomEmojiParams | null;
  readonly resizeButtonOpened: IField<boolean | null>;

  setCustomEmoji(value: MarkupCustomEmojiParams | null): void;
  setRef(ref: HTMLDivElement | null): void;
  handleFocus(): void;
  handleBlur(): void;
  initialize(): void;
  disposeAll(): void;
  toggleResizeButtonOpened(): void;
}

export interface IMarkupModel {
  readonly html: IField<string>;
  readonly isEmpty: boolean;
  readonly plainText: string;
  readonly error: ValidatorResult;
  readonly withVariables: boolean;
  readonly withSpoiler: boolean;

  setError(value: ValidatorResult): void;
  updateMarkup({ html, text }: { html: string; text: string }): void;
  checkVariables(): string | null;
  toJson(): MarkupServer;
}

export type MentionData = {
  name: string;
  description: string | null;
};

/** Сценарий inline-suggestion в Markup (% / %% / @ / /). */
export enum MarkupSuggestionKind {
  variableValue = 'variableValue',
  variableName = 'variableName',
  operatorMention = 'operatorMention',
  quickReplyTemplate = 'quickReplyTemplate'
}

export type MarkupSuggestionTriggerConfig = {
  kind: MarkupSuggestionKind;
  trigger: string;
  feature?: MarkupFeature;
};

export const variableValueSuggestionTrigger: MarkupSuggestionTriggerConfig = {
  kind: MarkupSuggestionKind.variableValue,
  trigger: '%',
  feature: MarkupFeature.vars
};

export const quickReplyTemplateSuggestionTrigger: MarkupSuggestionTriggerConfig =
  {
    kind: MarkupSuggestionKind.quickReplyTemplate,
    trigger: '/',
    feature: undefined
  };

export const operatorMentionSuggestionTrigger: MarkupSuggestionTriggerConfig = {
  kind: MarkupSuggestionKind.operatorMention,
  trigger: '@',
  feature: MarkupFeature.operatorMention
};
